import React from 'react';
import { useIntl, defineMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowTrendUp } from '@fortawesome/pro-light-svg-icons/faArrowTrendUp';
import { faWaveSquare } from '@fortawesome/pro-light-svg-icons/faWaveSquare';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import type { SummaryDoc, SummarySection, SummaryHighlight } from 'fym-shared/src/aiInsights/SummaryDoc';
import { TypedMemo } from '../../../common/types';
import { SMLLink } from '../SMLLink/SMLLink';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { renderBoldMarkdown } from './boldMarkdown';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';
/* eslint-disable css-modules/no-unused-class */
import styles from './TrendsSummaryPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

const messages = {
  kicker: defineMessage({ id: 'app.sml.aiInsights.kicker', defaultMessage: 'AI daily brief' }),
};

interface Props {
  summary: SummaryDoc;
}

const sectionIcon = (kind: SummarySection['kind']) =>
  kind === 'artists-trending' || kind === 'tracks-trending' ? faArrowTrendUp : faWaveSquare;

const HighlightChip: React.FC<{ h: SummaryHighlight }> = ({ h }) => {
  let changeClass = '';
  if (h.direction === 'up') changeClass = styles.highlightUp ?? '';
  else if (h.direction === 'down') changeClass = styles.highlightDown ?? '';

  if (h.entityType === 'artist') {
    return (
      <SMLLink to={SMLRoutes.artistDetails} params={{ artistId: h.entityId }} className={styles.highlight}>
        {h.label}
        <span className={`${styles.highlightChange} ${changeClass}`}>{h.change}</span>
      </SMLLink>
    );
  }
  return (
    <SMLLink to={SMLRoutes.trackDetails} params={{ trackId: h.entityId }} className={styles.highlight}>
      {h.label}
      <span className={`${styles.highlightChange} ${changeClass}`}>{h.change}</span>
    </SMLLink>
  );
};

const SummaryReadyViewComponent: React.FC<Props> = ({ summary }) => {
  const intl = useIntl();
  return (
    <Panel className={styles.panel}>
      <PanelHead icon={<FontAwesomeIcon icon={faSparkles} />} title={intl.formatMessage(messages.kicker)} />
      <PanelBody>
        <h2 className={styles.headline}>{summary.headline}</h2>
        <p className={styles.lead}>{summary.lead}</p>
        <div className={styles.sections}>
          {summary.sections.map((section) => (
            <div key={section.kind} className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={sectionIcon(section.kind)} />
                {section.title}
              </h3>
              <p className={styles.sectionBody}>{renderBoldMarkdown(section.body)}</p>
              {section.highlights.length > 0 && (
                <ul className={styles.highlights}>
                  {section.highlights.map((h) => (
                    <li key={h.entityId}>
                      <HighlightChip h={h} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        {summary.callouts !== undefined && summary.callouts.length > 0 && (
          <div className={styles.callouts}>
            {summary.callouts.map((c) => (
              <div
                key={`${c.tone}-${c.text}`}
                className={`${styles.callout} ${c.tone === 'warn' ? styles.calloutWarn : ''}`}
              >
                {c.text}
              </div>
            ))}
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const SummaryReadyView = TypedMemo(SummaryReadyViewComponent);
